'use client';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import { MouseEventHandler, useEffect } from 'react';
import { DangerAlert } from './danger-alert';
import InfoAlert from './info-alert';
import SuccessAlert from './success-alert';
import WarningAlert from './warning-alert';

export interface AlertProps {
  label: string;
  type: AlertTypes;
  show: boolean;
  close: MouseEventHandler<HTMLButtonElement>;
  position?: 'top' | 'bottom';
  duration?: number;
}

export enum AlertTypes {
  INFO = 'info',
  WARNING = 'warning',
  DANGER = 'danger',
  SUCCESS = 'success',
}

export default function Alert(props: AlertProps) {
  const { label, type, show, close, duration, position } = props;

  useEffect(() => {
    if (show && duration && close) {
      const timer = setTimeout(close, duration);

      return () => clearTimeout(timer);
    }
  }, [show, duration, close]);

  return (
    <div
      className={clsx({
        'fixed bottom-24 left-1/2 z-[9999999] flex -translate-x-1/2': position === 'bottom',
        'fixed left-1/2 top-24 z-[9999999] flex -translate-x-1/2': position === 'top',
      })}
    >
      <Transition
        show={!!show}
        as={'div'}
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-0"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        {
          {
            info: <InfoAlert label={label} close={close} />,
            warning: <WarningAlert label={label} close={close} />,
            danger: <DangerAlert label={label} close={close} />,
            success: <SuccessAlert label={label} close={close} />,
          }[type]
        }
      </Transition>
    </div>
  );
}
